import React from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  ContentCut as ContentCutIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  LocalHospital as HealthIcon,
  FitnessCenter as FitnessIcon,
  Spa as SpaIcon,
  Face as BeautyIcon,
  School as EducationIcon,
  Build as MaintenanceIcon,
  Computer as TechIcon,
  Restaurant as FoodIcon,
  DirectionsCar as TransportIcon,
  Home as HomeIcon,
  Pets as PetsIcon,
  ChildCare as ChildCareIcon,
  RemoveCircle as RemoveCircleIcon,
} from '@mui/icons-material';
import { usePlacesData } from '../../contexts/PlacesDataContext';
import { useThemeCustom } from '../../hooks/useThemeCustom';
import PaymentIcon from '../ui/PaymentIcon';
import { getInitials } from '../../utils/professionalNameUtils';
import ProfessionalAvatar from '../ProfessionalAvatar';
import { getIconClass } from '../../utils/iconUtils';

/**
 * Retorna ícone apropriado baseado na categoria do serviço
 */
const getCategoryIcon = (category) => {
  if (!category) return ContentCutIcon;
  
  const categoryLower = category.toLowerCase();
  
  // Saúde e Medicina
  if (categoryLower.includes('saúde') || categoryLower.includes('saude') || 
      categoryLower.includes('médico') || categoryLower.includes('medico') ||
      categoryLower.includes('clínica') || categoryLower.includes('clinica') ||
      categoryLower.includes('hospital') || categoryLower.includes('odonto')) {
    return HealthIcon;
  }
  
  // Beleza e Estética
  if (categoryLower.includes('beleza') || categoryLower.includes('estética') || 
      categoryLower.includes('estetica') || categoryLower.includes('cabelo') ||
      categoryLower.includes('manicure') || categoryLower.includes('pedicure') ||
      categoryLower.includes('maquiagem') || categoryLower.includes('salão') ||
      categoryLower.includes('salao') || categoryLower.includes('barbeiro')) {
    return BeautyIcon;
  }
  
  // Bem-estar e Spa
  if (categoryLower.includes('spa') || categoryLower.includes('massagem') ||
      categoryLower.includes('relaxamento') || categoryLower.includes('terapia')) {
    return SpaIcon;
  }
  
  // Fitness e Esportes
  if (categoryLower.includes('fitness') || categoryLower.includes('academia') ||
      categoryLower.includes('personal') || categoryLower.includes('esporte') ||
      categoryLower.includes('yoga') || categoryLower.includes('pilates')) {
    return FitnessIcon;
  }
  
  // Educação e Cursos
  if (categoryLower.includes('educação') || categoryLower.includes('educacao') ||
      categoryLower.includes('curso') || categoryLower.includes('aula') ||
      categoryLower.includes('ensino') || categoryLower.includes('escola')) {
    return EducationIcon;
  }
  
  // Tecnologia e Informática
  if (categoryLower.includes('tecnologia') || categoryLower.includes('informática') ||
      categoryLower.includes('informatica') || categoryLower.includes('computador') ||
      categoryLower.includes('software') || categoryLower.includes('ti')) {
    return TechIcon;
  }
  
  // Manutenção e Reparos
  if (categoryLower.includes('manutenção') || categoryLower.includes('manutencao') ||
      categoryLower.includes('reparo') || categoryLower.includes('conserto') ||
      categoryLower.includes('instalação') || categoryLower.includes('instalacao')) {
    return MaintenanceIcon;
  }
  
  // Alimentação e Gastronomia
  if (categoryLower.includes('alimentação') || categoryLower.includes('alimentacao') ||
      categoryLower.includes('restaurante') || categoryLower.includes('comida') ||
      categoryLower.includes('gastronomia') || categoryLower.includes('chef')) {
    return FoodIcon;
  }
  
  // Transporte
  if (categoryLower.includes('transporte') || categoryLower.includes('motorista') ||
      categoryLower.includes('táxi') || categoryLower.includes('taxi') ||
      categoryLower.includes('uber') || categoryLower.includes('delivery')) {
    return TransportIcon;
  }
  
  // Serviços Domésticos
  if (categoryLower.includes('doméstico') || categoryLower.includes('domestico') ||
      categoryLower.includes('limpeza') || categoryLower.includes('casa') ||
      categoryLower.includes('lar')) {
    return HomeIcon;
  }
  
  // Pet Services
  if (categoryLower.includes('pet') || categoryLower.includes('animal') ||
      categoryLower.includes('veterinário') || categoryLower.includes('veterinario') ||
      categoryLower.includes('cachorro') || categoryLower.includes('gato')) {
    return PetsIcon;
  }
  
  // Cuidados Infantis
  if (categoryLower.includes('infantil') || categoryLower.includes('criança') ||
      categoryLower.includes('crianca') || categoryLower.includes('bebê') ||
      categoryLower.includes('bebe') || categoryLower.includes('babá')) {
    return ChildCareIcon;
  }
  
  // Default
  return ContentCutIcon;
};

/**
 * PlaceCardNormalized
 * 
 * Card que usa estrutura normalizada com lookup de dados via context
 * Não faz mais chamadas API individuais - usa cache local
 */
export default function PlaceCardNormalized({
  place,
  onEdit,
  onDelete,
  onConfigurePayments,
  onManageServices,
  onManageProfessionals,
  onRemoveService,
  onRemoveProfessional,
  onConfigureProfessionalServices
}) {
  const { getService, getPayment, getProfessional } = usePlacesData();
  const { getIcon } = useThemeCustom();
  const PlaceIconCmp = getIconClass(getIcon('places').name);

  const profCount = place.professionals?.length || 0;
  const svcCount = place.services?.length || 0;

  const renderProfessionalRow = (link) => {
    const professional = getProfessional(link.professionalId);
    if (!professional) return null;

    const initials = getInitials(professional.name || 'Profissional');
    const expandedLink = {
      ...link,
      professional,
      professionalName: professional?.name,
    };
    const profServices = (link.serviceIds || [])
      .map((serviceId) => getService(serviceId))
      .filter(Boolean);
    const subtitle = profServices.length > 0
      ? profServices.map((s) => s.name).join(', ')
      : (professional.specialty || 'Nenhum serviço');

    return (
      <Box
        key={link.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.25,
          px: 0.5,
          borderRadius: 0.5,
          minWidth: 0,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <ProfessionalAvatar
          professionalId={professional.id}
          photoTimestamp={professional.photoTimestamp}
          name={professional.name}
          sx={{
            bgcolor: 'primary.main',
            width: 28,
            height: 28,
            fontSize: '0.72rem',
            flexShrink: 0,
          }}
        >
          {initials}
        </ProfessionalAvatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}
          >
            {professional.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block', fontSize: '0.67rem', lineHeight: 1.2 }}
          >
            {subtitle}
          </Typography>
        </Box>
        <Tooltip title={profServices.length > 0 ? 'Configurar serviços do profissional' : 'Adicionar serviços'}>
          <IconButton
            size="small"
            onClick={() => onConfigureProfessionalServices(place, expandedLink)}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            {profServices.length > 0
              ? <SettingsIcon sx={{ fontSize: 14 }} />
              : <AddIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Remover profissional do local">
          <IconButton
            size="small"
            color="error"
            onClick={() => onRemoveProfessional(place, expandedLink)}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <RemoveCircleIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const renderServiceRow = (serviceLink) => {
    const service = getService(serviceLink.serviceId);
    if (!service) return null;

    const CategoryIcon = getCategoryIcon(service.category);
    const expandedServiceLink = {
      ...serviceLink,
      service,
      serviceName: service?.name,
    };
    const allPayments = (serviceLink.payments || [])
      .map((pl) => {
        const payment = getPayment(pl.paymentId);
        return payment ? { ...payment, linkId: pl.id } : null;
      })
      .filter(Boolean)
      .sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
    const visiblePayments = allPayments.slice(0, 3);
    const extraPayments = allPayments.length - visiblePayments.length;

    return (
      <Box
        key={serviceLink.id}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          py: 0.25,
          px: 0.5,
          borderRadius: 0.5,
          minWidth: 0,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <CategoryIcon sx={{ fontSize: 15, color: 'action.active', flexShrink: 0 }} />
        <Typography
          variant="body2"
          noWrap
          sx={{ flex: 1, minWidth: 0, fontSize: '0.8rem' }}
        >
          {service.name}
        </Typography>
        {allPayments.length > 0 ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
            {visiblePayments.map((payment) => (
              <Tooltip key={payment.linkId} title={payment.name}>
                <Box sx={{ display: 'flex' }}>
                  <PaymentIcon displayOrder={payment.displayOrder} size={16} />
                </Box>
              </Tooltip>
            ))}
            {extraPayments > 0 && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.62rem' }}
              >
                +{extraPayments}
              </Typography>
            )}
          </Box>
        ) : (
          <Tooltip title="Nenhuma forma de pagamento configurada">
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'warning.main',
                flexShrink: 0,
              }}
            />
          </Tooltip>
        )}
        <Tooltip title="Configurar formas de pagamento">
          <IconButton
            size="small"
            onClick={() => onConfigurePayments(serviceLink)}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <SettingsIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remover serviço do local">
          <IconButton
            size="small"
            color="error"
            onClick={() => onRemoveService(place, expandedServiceLink)}
            sx={{ p: 0.25, flexShrink: 0 }}
          >
            <RemoveCircleIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const columnHeader = (icon, label, count, onAdd, addTitle) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        mb: 0.5,
        pb: 0.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {icon}
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          fontSize: '0.66rem',
          flex: 1,
          color: 'text.primary',
        }}
      >
        {label} ({count})
      </Typography>
      <Tooltip title={addTitle}>
        <IconButton size="small" onClick={onAdd} sx={{ p: 0.25 }}>
          <AddIcon sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );

  const columnBoxSx = {
    minWidth: 0,
    p: 0.75,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: (theme) => theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.035)'
      : 'rgba(99, 115, 129, 0.06)',
  };

  return (
    <Card
      elevation={0}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: 1.25,
        gap: 0.75,
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: 'primary.main',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': {
          boxShadow: 2,
          borderColor: 'primary.light',
          borderLeftColor: 'primary.main',
        },
      }}
    >
      {/* Header: icon + name + actions */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexShrink: 0 }}>
        <PlaceIconCmp sx={{ fontSize: 22, color: 'primary.main', flexShrink: 0 }} />
        <Tooltip title={place.description || ''} disableHoverListener={!place.description}>
          <Typography
            variant="subtitle1"
            fontWeight={700}
            noWrap
            sx={{ fontSize: '1.05rem', flex: 1, minWidth: 0, lineHeight: 1.25 }}
          >
            {place.name}
          </Typography>
        </Tooltip>
        <Box sx={{ display: 'flex', flexShrink: 0 }}>
          <Tooltip title="Editar local">
            <IconButton size="small" onClick={() => onEdit(place)} color="primary" sx={{ p: 0.5 }}>
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir local">
            <IconButton size="small" onClick={() => onDelete(place)} color="error" sx={{ p: 0.5 }}>
              <DeleteIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Meta row: address + phone compact */}
      {(place.address || place.phone) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0,
            flexShrink: 0,
            color: 'text.secondary',
          }}
        >
          {place.address && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0, flex: 1 }}>
              <LocationIcon sx={{ fontSize: 13, flexShrink: 0 }} />
              <Typography variant="caption" noWrap sx={{ fontSize: '0.72rem' }}>
                {place.address}
              </Typography>
            </Box>
          )}
          {place.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
              <PhoneIcon sx={{ fontSize: 13 }} />
              <Typography variant="caption" sx={{ fontSize: '0.72rem' }}>
                {place.phone}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Two-column content: Profissionais | Serviços (stacked em telas pequenas) */}
      <Box
        sx={{
          mt: 0.25,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          columnGap: 1,
          rowGap: 1,
          maxHeight: 280,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.5,
          mr: -0.5,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'action.disabledBackground',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'action.disabled' },
        }}
      >
        {/* Profissionais */}
        <Box sx={columnBoxSx}>
          {columnHeader(
            <PersonIcon sx={{ fontSize: 14, color: 'primary.main' }} />,
            'Profissionais',
            profCount,
            () => onManageProfessionals(place),
            'Gerenciar profissionais'
          )}
          {profCount > 0 ? (
            place.professionals.map(renderProfessionalRow)
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', fontSize: '0.7rem', display: 'block', px: 0.5 }}
            >
              Nenhum profissional vinculado
            </Typography>
          )}
        </Box>

        {/* Serviços */}
        <Box sx={columnBoxSx}>
          {columnHeader(
            <ContentCutIcon sx={{ fontSize: 14, color: 'primary.main' }} />,
            'Serviços',
            svcCount,
            () => onManageServices(place),
            'Gerenciar serviços'
          )}
          {svcCount > 0 ? (
            place.services.map(renderServiceRow)
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontStyle: 'italic', fontSize: '0.7rem', display: 'block', px: 0.5 }}
            >
              Nenhum serviço vinculado
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
}

PlaceCardNormalized.propTypes = {
  place: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onConfigurePayments: PropTypes.func,
  onManageServices: PropTypes.func,
  onManageProfessionals: PropTypes.func,
  onRemoveService: PropTypes.func,
  onRemoveProfessional: PropTypes.func,
  onConfigureProfessionalServices: PropTypes.func,
};
